import { getConfig } from '@superbet-group/web.lib.config';
import {
    createSimpleGetter,
    createSimpleMutator,
} from '@superbet-group/web.lib.vue-utilities';
import {
    ActionContext,
    Commit,
} from 'vuex';

import { IState } from '@app/store';
import {
    BetlerBonus,
    Bonus,
    ComtradeBonus,
} from '@core/models/bonus/Bonus';
import BonusProvider from '@core/models/bonus/BonusProvider';
import { getPlayerBonuses } from '@core/services/bonus/BonusService';
import {
    adjustActiveBonuses,
    orderActiveBonuses,
    sortByTypeAndGroupByCategory,
    sortChronologically,
} from '@store/modules/ui/user/bonuses/helpers';

import { BonusState } from './bonusTypes';
import BonusCategory from '@core/models/bonus/BonusCategory';
import { BonusStatus } from '@core/models/bonus/BonusStatus';
import PushNotificationsService from '@core/services/pushNotifications/PushNotificationsService';
import { PushEventType } from '@core/services/pushNotifications/types';
import { RouteName } from '@src/app/router/constants/RouteName';

const SET_ACTIVE_BONUSES = 'SET_ACTIVE_BONUSES';
const SET_PENDING_BONUSES = 'SET_PENDING_BONUSES';
const SET_HISTORY_BONUSES = 'SET_HISTORY_BONUSES';
const SET_ARE_BONUSES_FETCHED = 'SET_ARE_BONUSES_FETCHED';

function defaultState(): BonusState {
    return {
        activeBonuses: [],
        pendingBonuses: [],
        historyBonuses: [],
        areBonusesFetched: false,
    };
}

export default {
    namespaced: true,
    state: defaultState(),
    mutations: {
        [SET_ACTIVE_BONUSES]: createSimpleMutator<BonusState, 'activeBonuses'>('activeBonuses'),
        [SET_PENDING_BONUSES]: createSimpleMutator<BonusState, 'pendingBonuses'>('pendingBonuses'),
        [SET_HISTORY_BONUSES]: createSimpleMutator<BonusState, 'historyBonuses'>('historyBonuses'),
        [SET_ARE_BONUSES_FETCHED]: createSimpleMutator<BonusState, 'areBonusesFetched'>('areBonusesFetched'),
    },
    getters: {
        pendingBonuses: createSimpleGetter<BonusState, 'pendingBonuses'>('pendingBonuses'),
    },
    actions: {
        async fetchBonuses(context: ActionContext<BonusState, IState>, { shouldUpdateBalance = false, includeAwardingConditions = false }) {
            const { commit } = context;

            const bonuses = await getPlayerBonuses(includeAwardingConditions);
            context.commit(SET_ARE_BONUSES_FETCHED, true);

            const { activeBonuses, pendingBonuses, historyBonuses } = sortByTypeAndGroupByCategory(bonuses);

            let orderedActiveBonuses: Bonus[] = [];
            if (bonuses) {
                if (getConfig().countrySpecific.user.bonus.bonusProvider === BonusProvider.Comtrade) {
                    const unadjustedActiveBonuses = bonuses.filter((bonus) => bonus.category && bonus.category !== BonusCategory.History && bonus.status !== BonusStatus.Waiting);
                    orderedActiveBonuses = await adjustActiveBonuses(context, unadjustedActiveBonuses as ComtradeBonus[], shouldUpdateBalance);
                } else {
                    orderedActiveBonuses = orderActiveBonuses(activeBonuses as BetlerBonus[]);
                }
            }
            commit(SET_ACTIVE_BONUSES, orderedActiveBonuses);
            commit(SET_PENDING_BONUSES, sortChronologically(pendingBonuses));
            commit(SET_HISTORY_BONUSES, sortChronologically(historyBonuses));
        },
        async refreshActiveBonuses(context: ActionContext<BonusState, IState>) {
            const adjustedBonuses = await adjustActiveBonuses(context, context.state.activeBonuses as ComtradeBonus[], false);
            context.commit(SET_ACTIVE_BONUSES, adjustedBonuses);
        },
        setAreBonusesFetched({ commit }: { commit: Commit }, value: boolean) {
            commit(SET_ARE_BONUSES_FETCHED, value);
        },
        subscribeToBonusChange(context: ActionContext<BonusState, IState>) {
            PushNotificationsService.getInstance().subscribe(PushEventType.BonusUpdate, async () => {
                const currentRoute = context.rootGetters['navigation/route'];
                await context.dispatch('ui/user/account/getBalance', { shouldCheckBonus: true }, { root: true });
                if (currentRoute.name === RouteName.BonusAndComms.PromotionHub) {
                    context.dispatch('ui/user/promotions/fetchPromotions', null, { root: true });
                }
            });
        },
    },
};
